import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { 
  User,
  Lock,
  Mail,
  Calendar,
  Shield,
  Save,
  Loader2,
  CheckCircle2,
  Camera,
  Trash2,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/profile`, { name: profileData.name }, getAuthHeaders());
      toast.success('Profile updated successfully');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API_URL}/profile/password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, getAuthHeaders());
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WEBP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setAvatarLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API_URL}/profile/avatar`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Profile image uploaded successfully');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setAvatarLoading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    setAvatarLoading(true);
    try {
      await axios.delete(`${API_URL}/profile/avatar`, getAuthHeaders());
      toast.success('Profile image removed');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to remove image');
    } finally {
      setAvatarLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-blue-100 text-blue-700';
      case 'team': return 'bg-green-100 text-green-700';
      case 'client': return 'bg-slate-100 text-slate-700';
      case 'client_team': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getInitials = () => {
    return user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className={`text-2xl font-bold ${isDark ? 'text-slate-100' : 'text-slate-900'}`} style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Profile
        </h1>
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>View and manage your account information</p>
      </div>

      {/* Profile Overview Card */}
      <Card className={`shadow-sm overflow-hidden ${isDark ? 'bg-slate-800 border-slate-700' : 'border-slate-200'}`}>
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-8">
          <div className="flex items-center gap-6">
            {/* Avatar with upload */}
            <div className="relative group">
              <div 
                className={`w-24 h-24 rounded-full overflow-hidden border-4 border-white/20 ${
                  avatarLoading ? 'opacity-50' : ''
                }`}
              >
                {user?.avatar_url ? (
                  <img 
                    src={user.avatar_url} 
                    alt={user.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-white/10 backdrop-blur flex items-center justify-center">
                    <span className="text-3xl font-bold text-white">
                      {getInitials()}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Upload overlay */}
              <div 
                className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={handleAvatarClick}
              >
                {avatarLoading ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
              </div>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
                data-testid="avatar-upload-input"
              />
              
              {/* Remove button */}
              {user?.avatar_url && !avatarLoading && (
                <button
                  onClick={handleAvatarRemove}
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg transition-colors"
                  title="Remove photo"
                  data-testid="remove-avatar-btn"
                >
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {user?.name}
              </h2>
              <p className="text-slate-300 flex items-center gap-2 mt-1">
                <Mail size={14} />
                {user?.email}
              </p>
              <span className={`inline-block mt-3 px-3 py-1 text-xs font-medium rounded-full capitalize ${getRoleBadgeColor(user?.role)}`}>
                <Shield size={12} className="inline mr-1" />
                {user?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
          
          {/* Upload hint */}
          <p className="text-slate-400 text-xs mt-4 flex items-center gap-1">
            <Upload size={12} />
            Hover over your photo to change it. Max 5MB (JPEG, PNG, GIF, WEBP)
          </p>
        </div>
        <CardContent className={`p-6 ${isDark ? 'bg-slate-800' : ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-green-900/30' : 'bg-green-50'}`}>
                <CheckCircle2 size={20} className={isDark ? 'text-green-400' : 'text-green-600'} />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Email Status</p>
                <p className={`font-medium ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                  {user?.is_verified ? 'Verified' : 'Pending Verification'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                <Calendar size={20} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Member Since</p>
                <p className={`font-medium ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                <User size={20} className={isDark ? 'text-purple-400' : 'text-purple-600'} />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Last Login</p>
                <p className={`font-medium ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                  {user?.last_login ? new Date(user.last_login).toLocaleDateString() : 'Just now'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Profile Section */}
      <Card className={`shadow-sm ${isDark ? 'bg-slate-800 border-slate-700' : 'border-slate-200'}`}>
        <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
          <CardTitle className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            <User size={20} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
            Edit Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-200' : ''}>Full Name</Label>
              <Input 
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600 text-slate-100' : ''}
                data-testid="profile-name-input" 
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-200' : ''}>Email Address</Label>
              <Input 
                type="email" 
                value={user?.email} 
                disabled 
                className={isDark ? 'bg-slate-900 border-slate-700 text-slate-400' : 'bg-slate-50'}
              />
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Email cannot be changed</p>
            </div>
          </div>
          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleProfileSave} 
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-profile-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Section */}
      <Card className={`shadow-sm ${isDark ? 'bg-slate-800 border-slate-700' : 'border-slate-200'}`}>
        <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
          <CardTitle className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            <Lock size={20} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-200' : ''}>Current Password</Label>
                <Input 
                  type="password" 
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter current password" 
                  required
                  className={isDark ? 'bg-slate-700 border-slate-600 text-slate-100' : ''}
                  data-testid="current-password-input" 
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-200' : ''}>New Password</Label>
                <Input 
                  type="password" 
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password" 
                  required
                  className={isDark ? 'bg-slate-700 border-slate-600 text-slate-100' : ''}
                  data-testid="new-password-input" 
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-200' : ''}>Confirm New Password</Label>
                <Input 
                  type="password" 
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password" 
                  required
                  className={isDark ? 'bg-slate-700 border-slate-600 text-slate-100' : ''}
                  data-testid="confirm-password-input" 
                />
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button 
                type="submit"
                disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="change-password-btn"
              >
                {passwordLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
